import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../services/notificationService";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  // Grid removed from here; using Grid2 below
  LinearProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { auth } from "../firebase";

const CampaignListPage = () => {
  const navigate = useNavigate();

  // State Management
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load campaigns
  useEffect(() => {
    const loadCampaigns = async () => {
      setLoading(true);
      try {
        // Fetch campaigns from backend with auth token
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error("Kullanıcı girişi yapılmamış");
        }
        const idToken = await currentUser.getIdToken();

        const response = await fetch(`${API_BASE_URL}/api/campaigns`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Campaign verilerini yükleyemedi");
        }

        const data = await response.json();
        console.log("📋 Campaigns yüklendi:", data);

        setCampaigns(data.campaigns || []);
      } catch (error) {
        console.error("Error loading campaigns:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  // Filter campaigns based on search term
  const filteredCampaigns = campaigns.filter((campaign) => {
    const name = campaign.name || "";
    const id = campaign.id || "";
    const query = campaign.metadata?.query || "";
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return (
      name.toLowerCase().includes(lowerCaseSearchTerm) ||
      id.toLowerCase().includes(lowerCaseSearchTerm) ||
      query.toLowerCase().includes(lowerCaseSearchTerm)
    );
  });

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "active":
        return "primary";
      case "processing":
        return "warning";
      case "failed":
        return "error";
      case "paused":
        return "default";
      default:
        return "default";
    }
  };

  // Status text mapping
  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Tamamlandı";
      case "active":
        return "Aktif";
      case "processing":
        return "İşleniyor";
      case "failed":
        return "Başarısız";
      case "paused":
        return "Duraklatıldı";
      default:
        return status;
    }
  };

  // Handle menu actions
  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  // Navigation handlers
  const handleViewCampaign = (campaignId) => {
    navigate(`/campaigns/${campaignId}`);
    handleMenuClose();
  };

  const handleEditCampaign = (campaignId) => {
    navigate(`/campaigns/${campaignId}/edit`);
    handleMenuClose();
  };

  const handleDeleteCampaign = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (campaignToDelete) {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setSnackbar({
            open: true,
            message: "Oturum bulunamadı. Lütfen yeniden giriş yapın.",
            severity: "error",
          });
          setDeleteDialogOpen(false);
          setCampaignToDelete(null);
          return;
        }
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/api/campaigns/${campaignToDelete.id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Kampanya silinemedi.");
        }

        setCampaigns((prev) =>
          prev.filter((c) => c.id !== campaignToDelete.id)
        );
        setSnackbar({
          open: true,
          message: "Kampanya başarıyla silindi!",
          severity: "success",
        });
      } catch (error) {
        console.error("Delete error:", error);
        setSnackbar({
          open: true,
          message: `Hata: ${error.message}`,
          severity: "error",
        });
      }
    }
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  // Campaign status actions
  const handleStatusAction = async (campaignId, action) => {
    try {
      console.log(`${action} campaign:`, campaignId);
      // Here you would make API call to update campaign status
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === campaignId
            ? {
                ...c,
                status:
                  action === "play"
                    ? "active"
                    : action === "pause"
                    ? "paused"
                    : "stopped",
              }
            : c
        )
      );
    } catch (error) {
      console.error("Status update error:", error);
    }
    handleMenuClose();
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      let date;
      
      // Handle Firestore Timestamp
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } 
      // Handle ISO string
      else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      }
      // Handle seconds/nanoseconds object
      else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Already a Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else {
        return "Geçersiz Tarih";
      }
      
      if (isNaN(date.getTime())) return "Geçersiz Tarih";
      return date.toLocaleString("tr-TR");
    } catch (e) {
      return "Geçersiz Tarih";
    }
  };

  // Calculate success rate
  const getSuccessRate = (campaign) => {
    const total = campaign.totalPins || 0;
    const successful = campaign.successfulPins || 0;
    
    if (total === 0) return 0;
    return Math.round((successful / total) * 100);
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Campaign Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/campaigns/new")}
        >
          Yeni Campaign
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {campaigns.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Campaign
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {campaigns.filter((c) => c.status === "active").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aktif Campaign
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {campaigns.reduce((sum, c) => sum + c.totalPins, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Pin
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {campaigns.reduce(
                  (sum, c) => sum + (c.stats?.totalViews || 0),
                  0
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Görüntüleme
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Campaign ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Yenile
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Export
          </Button>
        </Box>
      </Paper>

      {/* Campaigns Table */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
              Campaign'ler yükleniyor...
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign Adı</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Pin Sayısı</TableCell>
                  <TableCell>Başarı Oranı</TableCell>
                  <TableCell>Oluşturma Tarihi</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Görüntüleme</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleViewCampaign(campaign.id)}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {campaign.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(campaign.status)}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {campaign.totalPins} toplam
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.successfulPins} başarılı,{" "}
                          {campaign.failedPins} hata
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={getSuccessRate(campaign)}
                          sx={{ width: 60, height: 6 }}
                          color={
                            getSuccessRate(campaign) > 90
                              ? "success"
                              : getSuccessRate(campaign) > 70
                              ? "warning"
                              : "error"
                          }
                        />
                        <Typography variant="caption">
                          {getSuccessRate(campaign)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(campaign.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          campaign.metadata?.category ||
                          campaign.category ||
                          "Standard"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.stats?.totalViews || 0}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, campaign)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && filteredCampaigns.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {searchTerm
                ? "Arama kriterlerine uygun campaign bulunamadı."
                : "Henüz campaign oluşturulmamış."}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewCampaign(selectedCampaign?.id)}>
          <ViewIcon sx={{ mr: 1 }} />
          Görüntüle
        </MenuItem>
        <MenuItem onClick={() => handleEditCampaign(selectedCampaign?.id)}>
          <EditIcon sx={{ mr: 1 }} />
          Düzenle
        </MenuItem>
        {selectedCampaign?.status === "active" && (
          <MenuItem
            onClick={() => handleStatusAction(selectedCampaign?.id, "pause")}
          >
            <PauseIcon sx={{ mr: 1 }} />
            Duraklat
          </MenuItem>
        )}
        {selectedCampaign?.status === "paused" && (
          <MenuItem
            onClick={() => handleStatusAction(selectedCampaign?.id, "play")}
          >
            <PlayIcon sx={{ mr: 1 }} />
            Başlat
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleStatusAction(selectedCampaign?.id, "stop")}
        >
          <StopIcon sx={{ mr: 1 }} />
          Durdur
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteCampaign(selectedCampaign)}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Sil
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Campaign Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography>
            "{campaignToDelete?.name}" campaign'ini silmek istediğinizden emin
            misiniz?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Bu işlem geri alınamaz. Campaign'e ait tüm pinler de silinecektir.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignListPage;
